<template>
  <v-container fluid class="pa-0 race-bg" style="min-height: 100vh;">
    <!-- App Bar -->
    <v-app-bar :elevation="2" :style="{ backgroundColor: colors.primary }">
      <v-app-bar-title :style="{ color: colors.secondary }">
        {{ $t('evaluation.title') }}
      </v-app-bar-title>
      <LanguageSwitcher />
      <div v-if="jury">
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props">
              <v-avatar size="36px">
                <v-img alt="Jury" :src="getJuryImageUrl(jury?.image)"></v-img>
              </v-avatar>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="goToProfile">
              <v-list-item-title>{{ $t('menu.myProfile') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="goToEvaluate" v-if="canAddMonthlyData || canAddPitchData">
              <v-list-item-title>{{ $t('menu.evaluation') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="logoutUser">
              <v-list-item-title>{{ $t('menu.logout') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
      <div v-else>
        <v-btn @click="$router.push({ name: 'WelcomeScreen' })" icon="mdi-home" color="white"></v-btn>
      </div>
    </v-app-bar>

    <div class="race-content">
      <!-- Title + Refresh -->
      <div class="race-title-row">
        <h1 class="race-title">{{ $t('results.title') }}</h1>
        <v-btn icon variant="text" color="white" @click="fetchLeaderboard" :loading="isLoading">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <!-- Filters -->
      <div class="filters-row">
        <div class="filter-group">
          <span class="filter-label">{{ $t('results.filterYear') }}</span>
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            variant="outlined"
            density="compact"
            hide-details
            bg-color="white"
            style="min-width: 110px;"
            @update:model-value="fetchLeaderboard"
          ></v-select>
        </div>
        <div class="filter-group">
          <span class="filter-label">{{ $t('results.filterMonth') }}</span>
          <v-select
            v-model="selectedMonth"
            :items="monthOptions"
            variant="outlined"
            density="compact"
            hide-details
            item-title="label"
            item-value="value"
            bg-color="white"
            style="min-width: 170px;"
            @update:model-value="fetchLeaderboard"
          ></v-select>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="d-flex justify-center align-center py-16">
        <v-progress-circular indeterminate color="white" size="56"></v-progress-circular>
      </div>

      <!-- No data -->
      <div v-else-if="!leaderboard.length" class="text-center py-16">
        <v-icon size="56" color="grey-lighten-1">mdi-trophy-outline</v-icon>
        <p class="text-grey-lighten-1 mt-3">{{ $t('results.noData') }}</p>
      </div>

      <!-- Race Results List -->
      <div v-else class="race-list">
        <div
          v-for="item in leaderboard"
          :key="item.candidate_id"
          class="race-card"
          :class="getRaceRowClass(item.rank, item.total_score)"
        >
          <!-- Main row: pos + flag + name + bar + total -->
          <div class="race-main-row">
            <!-- Position badge -->
            <div class="pos-badge" :class="getPosBadgeClass(item.rank, item.total_score)">
              <v-icon v-if="item.rank === 1 && item.total_score > 0" size="16" color="amber-darken-2">mdi-crown</v-icon>
              <span v-else>{{ item.rank }}</span>
            </div>

            <!-- Flag + Name -->
            <div class="race-flag-name">
              <v-avatar size="42" rounded="sm" class="race-flag">
                <v-img :src="getCandidateImageUrl(item.candidate_image)" :alt="item.candidate_name" cover></v-img>
              </v-avatar>
              <span class="race-name">{{ item.candidate_name }}</span>
            </div>

            <!-- Overall score bar -->
            <div class="race-bar-wrap">
              <v-progress-linear
                :model-value="pct(item.total_score, item.max_possible)"
                :color="getBarColor(item.rank, item.total_score)"
                bg-color="rgba(255,255,255,0.12)"
                rounded
                height="12"
              ></v-progress-linear>
            </div>

            <!-- Total score -->
            <div class="race-total" :class="getTotalClass(item.rank, item.total_score)">
              <span class="total-num">{{ item.total_score }}</span>
            </div>

            <!-- Expand button -->
            <v-btn
              icon
              size="small"
              variant="text"
              color="white"
              @click="toggleExpand(item.candidate_id)"
              style="flex-shrink:0;"
            >
              <v-icon size="18">{{ expandedIds.includes(item.candidate_id) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </div>

          <!-- Collapsible breakdown -->
          <v-expand-transition>
            <div v-if="expandedIds.includes(item.candidate_id)" class="race-breakdown">
              <!-- Individual monthly criteria -->
              <div
                v-for="cs in item.criteria_scores"
                :key="cs.criteria_id"
                class="bk-row"
              >
                <span class="bk-label">{{ isEnglish ? (cs.criteria_en || cs.criteria) : cs.criteria }}</span>
                <v-progress-linear
                  :model-value="pct(cs.score, cs.max)"
                  :color="cs.category === 'ANIMATION' ? 'green-lighten-1' : 'blue-lighten-1'"
                  bg-color="rgba(255,255,255,0.1)"
                  rounded height="7" class="bk-bar"
                ></v-progress-linear>
                <!-- Score chip — with tooltip when a specific month is selected -->
                <span
                  v-if="selectedMonth !== null"
                  class="bk-score bk-score--tip"
                  @mouseenter="showTooltip($event, cs.note)"
                  @mouseleave="hideTooltip"
                >{{ cs.score }}</span>
                <span v-else class="bk-score">{{ cs.score }}</span>
              </div>
              <!-- Pitch as single total -->
              <div class="bk-row">
                <span class="bk-label">{{ $t('results.pitch') }}</span>
                <v-progress-linear
                  :model-value="pct(item.pitch_score, item.pitch_max)"
                  color="purple-lighten-2"
                  bg-color="rgba(255,255,255,0.1)"
                  rounded height="7" class="bk-bar"
                ></v-progress-linear>
                <span class="bk-score">{{ item.pitch_score }}</span>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </div>
    </div>

    <v-footer border app class="d-flex justify-center align-center p-4">
      {{ $t('common.developedBy') }} :
      <a href="https://thewebsquare.com" target="_blank" :style="{ color: colors.primary }">The Web Square</a>
    </v-footer>

    <!-- Note tooltip -->
    <div v-if="tooltip.visible" class="note-tooltip" :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }">
      {{ tooltip.text }}
    </div>
  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import colors from '../styles/colors';
import axios from 'axios';
import api from '../api';
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  name: 'ResultsScreen',
  components: { LanguageSwitcher },

  data() {
    const currentYear = new Date().getFullYear();
    return {
      colors,
      isLoading: false,
      leaderboard: [],
      selectedYear: currentYear,
      selectedMonth: null,
      yearOptions: [currentYear - 1, currentYear, currentYear + 1],
      expandedIds: [],
      tooltip: { visible: false, text: '', x: 0, y: 0 },
    };
  },

  computed: {
    ...mapState(['jury']),
    ...mapGetters(['canAddMonthlyData', 'canAddPitchData']),

    isEnglish() {
      return this.$i18n.locale === 'en';
    },

    monthOptions() {
      const isFr = this.$i18n.locale === 'fr';
      const names = isFr
        ? ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre']
        : ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const opts = [{ label: this.$t('results.allMonths'), value: null }];
      names.forEach((name, i) => opts.push({ label: name, value: i + 1 }));
      return opts;
    },
  },

  methods: {
    ...mapActions(['logout']),
    getJuryImageUrl(f) { return api.getJuryImageUrl(f); },
    getCandidateImageUrl(f) { return api.getCandidateImageUrl(f); },

    pct(val, max) {
      if (!max) return 0;
      return Math.min(100, Math.round((val / max) * 100));
    },

    toggleExpand(candidateId) {
      const idx = this.expandedIds.indexOf(candidateId);
      if (idx >= 0) this.expandedIds.splice(idx, 1);
      else this.expandedIds.push(candidateId);
    },

    showTooltip(event, note) {
      const text = note && note.trim()
        ? note
        : (this.isEnglish ? 'No comment' : 'Aucun commentaire');
      const rect = event.target.getBoundingClientRect();
      this.tooltip = {
        visible: true,
        text,
        x: rect.left,
        y: rect.bottom + 6,
      };
    },

    hideTooltip() {
      this.tooltip.visible = false;
    },

    getRaceRowClass(rank, score) {
      if (!score) return '';
      if (rank === 1) return 'race-card--gold';
      if (rank === 2) return 'race-card--silver';
      if (rank === 3) return 'race-card--bronze';
      return '';
    },
    getPosBadgeClass(rank, score) {
      if (!score) return 'pos--other';
      if (rank === 1) return 'pos--gold';
      if (rank === 2) return 'pos--silver';
      if (rank === 3) return 'pos--bronze';
      return 'pos--other';
    },
    getTotalClass(rank, score) {
      if (!score) return 'total--other';
      if (rank === 1) return 'total--gold';
      if (rank === 2) return 'total--silver';
      if (rank === 3) return 'total--bronze';
      return 'total--other';
    },
    getBarColor(rank, score) {
      if (!score) return 'blue-lighten-2';
      if (rank === 1) return 'amber-darken-2';
      if (rank === 2) return 'grey-lighten-1';
      if (rank === 3) return 'brown-lighten-2';
      return 'blue-lighten-2';
    },

    async fetchLeaderboard() {
      this.isLoading = true;
      try {
        const params = { year: this.selectedYear };
        if (this.selectedMonth !== null) params.month = this.selectedMonth;
        const response = await axios.get(api.getLeaderboard, { params });
        this.leaderboard = response.data;
        // Change 5: expand all cards by default
        this.expandedIds = this.leaderboard.map(item => item.candidate_id);
      } catch (error) {
        console.error('Error fetching leaderboard:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async logoutUser() {
      try { await axios.post(api.juryLogout); } catch (e) { /* ignore */ }
      this.logout();
      this.$router.push({ name: 'JuryLogin' });
    },
    goToProfile() { this.$router.push({ name: 'JuryProfile' }); },
    goToEvaluate() { this.$router.push({ name: 'EvaluationScreen' }); },
  },

  mounted() { this.fetchLeaderboard(); },
};
</script>

<style scoped>
.race-bg {
  background: linear-gradient(160deg, #0a0f1e 0%, #111827 50%, #0a0f1e 100%);
}
.race-content {
  max-width: 860px;
  margin: 0 auto;
  padding: 24px 16px 80px;
}

/* Title */
.race-title-row {
  display: flex; align-items: center; justify-content: center;
  gap: 12px; margin-bottom: 20px; padding-top: 8px;
}
.race-title {
  color: #fff; font-size: 1.8rem; font-weight: 800;
  letter-spacing: 4px; text-transform: uppercase;
  text-shadow: 0 2px 12px rgba(0,0,0,.5); margin: 0;
}

/* Filters */
.filters-row {
  display: flex; flex-wrap: wrap; gap: 20px;
  justify-content: center; margin-bottom: 28px;
}
.filter-group { display: flex; flex-direction: column; gap: 6px; }
.filter-label {
  color: #fff; font-size: 0.76rem; font-weight: 600;
  letter-spacing: 1px; text-transform: uppercase;
}

/* Cards */
.race-list { display: flex; flex-direction: column; gap: 8px; }

.race-card {
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 12px;
  overflow: hidden;
  transition: background 0.2s;
}
.race-card:hover { background: rgba(255,255,255,0.09); }
.race-card--gold   { border-color: rgba(245,197,24,0.45); background: rgba(245,197,24,0.06); }
.race-card--silver { border-color: rgba(192,192,192,0.35); }
.race-card--bronze { border-color: rgba(205,127,50,0.35); background: rgba(205,127,50,0.04); }

/* Main row */
.race-main-row {
  display: flex; align-items: center;
  gap: 12px; padding: 14px 16px;
}

/* Position */
.pos-badge {
  width: 34px; height: 34px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; font-weight: 800; color: white; flex-shrink: 0;
}
.pos--gold   { background: linear-gradient(135deg,#f5c518,#d4a017); box-shadow: 0 0 10px rgba(245,197,24,.35); }
.pos--silver { background: linear-gradient(135deg,#c0c0c0,#909090); }
.pos--bronze { background: linear-gradient(135deg,#cd7f32,#a0522d); }
.pos--other  { background: rgba(255,255,255,0.15); }

/* Flag + name */
.race-flag-name {
  display: flex; align-items: center; gap: 9px;
  min-width: 130px; flex-shrink: 0;
}
.race-flag { border: 1px solid rgba(255,255,255,0.15); flex-shrink: 0; }
.race-name { color: #fff; font-weight: 700; font-size: 0.92rem; white-space: nowrap; }

/* Bar */
.race-bar-wrap { flex: 1; min-width: 0; }

/* Total */
.race-total { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; min-width: 64px; }
.total-num  { font-size: 1.4rem; font-weight: 900; line-height: 1; }
.total-max  { font-size: 0.68rem; color: rgba(255,255,255,0.35); margin-top: 1px; }
.total--gold   .total-num { color: #f5c518; }
.total--silver .total-num { color: #c8c8c8; }
.total--bronze .total-num { color: #cd9060; }
.total--other  .total-num { color: #90caf9; }

/* Breakdown */
.race-breakdown {
  padding: 0 16px 14px 16px;
  border-top: 1px solid rgba(255,255,255,0.07);
}
.bk-row {
  display: flex; align-items: center; gap: 8px;
  margin-top: 8px;
}
.bk-label {
  color: rgba(255,255,255,0.5); font-size: 0.69rem;
  text-transform: uppercase; letter-spacing: 0.5px;
  min-width: 120px; width: 120px; flex-shrink: 0; text-align: left;
}
.bk-bar { flex: 1; min-width: 0; }
.bk-score {
  color: rgba(255,255,255,0.75); font-size: 0.75rem;
  font-weight: 700; min-width: 46px; text-align: right; flex-shrink: 0;
}
.bk-score--tip {
  cursor: help;
  border-bottom: 1px dashed rgba(255,255,255,0.4);
}
.bk-max { color: rgba(255,255,255,0.35); font-weight: 400; }

/* Note tooltip */
.note-tooltip {
  position: fixed;
  z-index: 9999;
  background: rgba(15,15,30,0.97);
  color: #fff;
  font-size: 0.78rem;
  padding: 6px 10px;
  border-radius: 6px;
  max-width: 280px;
  white-space: pre-line;
  pointer-events: none;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  border: 1px solid rgba(255,255,255,0.12);
}

/* Mobile */
@media (max-width: 600px) {
  .race-main-row { flex-wrap: wrap; gap: 8px; padding: 10px 12px; }
  .race-flag-name { min-width: 110px; }
  .race-bar-wrap { order: 4; width: 100%; flex-basis: 100%; }
  .race-total { margin-left: auto; }
}
</style>
